/**
 * 創辦人理念 — 公開深度頁(不依賴 JWT,類似 Traceability)
 *
 * 從 LINE「霽雲・知遇」入口卡的「創辦理念」按鈕 → reply 兩張 carousel bubble,
 * 每張底部「閱讀完整理念」CTA → 跳這頁顯示完整《The Icons》專訪兩段原文 +
 * 謝睿朋的「情感落實為標準」段。
 *
 * 排版風格:雜誌專訪 — 大圖 Hero + 引言區塊(coral 邊條)+ 多段落內文。
 * URL hash 支援 #huang / #hsieh 從不同入口進來自動 scroll 到對應段落。
 */
import { useEffect } from "react";

const FOUNDER_HUANG_IMG = "https://storage.googleapis.com/yannwo-product-images/founder-huang-v2.jpg";
const FOUNDER_HSIEH_IMG = "https://storage.googleapis.com/yannwo-product-images/founder-hsieh-v2.jpg";

export default function BrandFounder() {
  // URL hash 跳轉 — 從 LINE bubble 點「閱讀完整理念」帶 #huang / #hsieh,進來自動 scroll
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // 等 DOM mount + 圖載入後 scroll
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream pb-16">
      {/* Header */}
      <header className="bg-primary-dark text-white px-6 py-10 text-center">
        <div className="text-xs tracking-widest opacity-70 uppercase">霽雲 · 恆時珍藏</div>
        <h1 className="text-3xl font-bold mt-3 tracking-wide">創辦人理念</h1>
        <div className="text-sm mt-2 opacity-80 tracking-wider">Founders&apos; Vision</div>
      </header>

      {/* 黃嘉琪 Section */}
      <section id="huang">
        <img
          src={FOUNDER_HUANG_IMG}
          alt="創辦人 黃嘉琪 Lydia"
          className="w-full aspect-[3/4] object-cover"
        />
        <div className="px-6 py-8">
          <div className="text-xs tracking-widest text-sage uppercase">Founder</div>
          <h2 className="text-2xl font-bold text-primary-dark mt-2">黃嘉琪 Lydia</h2>
          <div className="text-sm text-gray-mid mt-1 tracking-wide">
            靜奢主張 · 由內而外的當代奢華
          </div>

          {/* Pull quote */}
          <blockquote className="my-7 border-l-[3px] border-coral pl-5 py-1">
            <p className="text-lg leading-relaxed text-primary-dark">
              真正的奢華不再是外顯的符號,而是在高壓、多重角色的日常中,能長期維持身心的穩定、秩序與自我感。
            </p>
          </blockquote>

          {/* Body — 完整原文 */}
          <div className="space-y-5 text-[15px] leading-loose text-charcoal/85">
            <p>
              在《The Icons》的專訪中,創辦人黃嘉琪(Lydia)賦予了霽雲燕窩深厚的人文美學與核心理念:
            </p>
            <p>
              黃嘉琪主張現代潮流已從追求外在,轉向「由內而外的美」。她將擁有 1500 年歷史的東方滋補載體——燕窩,重新帶入當代國際語境,揉合東方美學與科學標準。她提出「靜奢(Quiet Luxury)」觀點,認為真正的奢華不再是外顯的符號,而是在高壓、多重角色的日常中,能長期維持身心的穩定、秩序與自我感。
            </p>
            <p>
              她特別關心在事業、人生或家庭間切換的女性,將燕窩轉化為一種專屬的日常儀式,讓健康成為最高級的社交資產,安靜卻持續地照顧個人長期狀態。
            </p>
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="px-6">
        <div className="border-t border-sage-light my-2"></div>
      </div>

      {/* 謝睿朋 Section */}
      <section id="hsieh">
        <img
          src={FOUNDER_HSIEH_IMG}
          alt="創辦人 謝睿朋 Eddie"
          className="w-full aspect-[3/4] object-cover"
        />
        <div className="px-6 py-8">
          <div className="text-xs tracking-widest text-sage uppercase">Founder</div>
          <h2 className="text-2xl font-bold text-primary-dark mt-2">謝睿朋 Eddie</h2>
          <div className="text-sm text-gray-mid mt-1 tracking-wide">
            家族傳承的健康防線 · 長期穩定的身心資產投資
          </div>

          <blockquote className="my-7 border-l-[3px] border-coral pl-5 py-1">
            <p className="text-lg leading-relaxed text-primary-dark">
              健康,才是企業風險控制中最容易被忽略、卻也最致命的缺口。
            </p>
          </blockquote>

          <div className="space-y-5 text-[15px] leading-loose text-charcoal/85">
            <p>
              謝睿朋 Eddie 指出,真正的家族傳承不僅是財富、土地或公司股權的交接,更核心的根基在於主事者的「生理與心理健康」。他認為,當創業者或家族核心人物面對不確定的市場環境與長期的身心消耗時,健康才是企業風險控制中最容易被忽略、卻也最致命的缺口。
            </p>
            <p>
              因此,謝睿朋推動霽雲燕窩建立極其嚴苛的供應鏈科學標準,將燕窩定位為一項「長期穩定的身心資產投資」。他將健康視為家族基業長青的防線,幫助高端客群在追求事業與財富傳承的同時,能擁有穩固的生理狀態作為支撐,實現全面性的家族繁榮與永續。
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-10 mt-4">
        <div className="text-xs text-gray-mid tracking-[0.3em]">
          霽雲 · 恆時珍藏
        </div>
      </footer>
    </div>
  );
}
